import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Landmark } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Panel, Stat, StatusPill, SandboxNotice, fmtMoney, fmtDate } from "@/components/Peep";
import { RevenueTable } from "@/pages/admin/Revenue";

export function PayoutPanel({ p }) {
  const navigate = useNavigate();
  const connect = async () => {
    try {
      const { data } = await api.post("/creator/payouts/connect");
      navigate(data.onboarding_url);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const a = p.account;
  return (
    <Panel title="Bank & payouts" action={<Landmark size={14} className="text-[#a7a0af]" />}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p data-testid="creator-bank-status" className="text-sm">{a ? `${a.bank_label || "Bank not verified yet"} · ${a.status}` : "No bank account connected"}</p>
        {!a?.payouts_enabled && <button onClick={connect} data-testid="creator-connect-bank" className="btn-brand rounded-xl px-4 py-2 text-xs font-semibold">Connect bank account</button>}
      </div>
      <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat label={`Pending (${p.hold_days}-day hold)`} value={fmtMoney(p.balances.pending)} testid="creator-bal-pending" />
        <Stat label="Available" value={fmtMoney(p.balances.available)} testid="creator-bal-available" />
        <Stat label="In payout" value={fmtMoney(p.balances.in_payout)} />
        <Stat label="Paid out" value={fmtMoney(p.balances.paid)} testid="creator-bal-paid" />
      </div>
      <div className="mt-4">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#c77dff]">How money reaches you</p>
        <ol data-testid="payout-stages" className="mt-2 grid gap-2 text-xs text-[#c7c1cf] md:grid-cols-4">
          <li className="rounded-xl border border-white/10 p-3"><b className="text-white">1. Settled</b><br />The fan's payment is confirmed by the processor (instant in the sandbox).</li>
          <li className="rounded-xl border border-white/10 p-3"><b className="text-white">2. Available</b><br />Your share becomes available after a {p.hold_days}-day hold for refunds and chargebacks.</li>
          <li className="rounded-xl border border-white/10 p-3"><b className="text-white">3. Payout started</b><br />PEEPING sends available earnings to your connected account (min {fmtMoney(p.min_payout)}).</li>
          <li className="rounded-xl border border-white/10 p-3"><b className="text-white">4. In your bank</b><br />Arrival time depends on the payout provider and your bank — not yet confirmed.</li>
        </ol>
        {p.schedule && <p data-testid="payout-schedule-note" className="mt-2 text-[0.7rem] text-[#a7a0af]">Planned: a {p.schedule.proposed_standard_days}-day hold for all creators and {p.schedule.proposed_qualified_days} days for qualifying creators. Status: {p.schedule.proposal_status}. The current hold remains {p.schedule.current_hold_days} days.</p>}
      </div>
      <div className="mt-4">{p.payouts.length === 0 ? <p className="text-xs text-[#a7a0af]">No payouts yet. PEEPING sends payouts from your available balance (minimum {fmtMoney(p.min_payout)}).</p> : p.payouts.map((x) => (
          <div key={x.id} className="flex justify-between border-t border-white/5 py-1.5 text-sm">
            <span>{fmtMoney(x.amount)} · {fmtDate(x.created_at)}</span><StatusPill status={x.status} />
          </div>
        ))}
      </div>
      <p className="mt-3 text-xs text-[#6f6878]">Bank details are entered only with the payment provider — PEEPING never stores them. Real payouts stay disabled until a compliant provider is approved.</p>
    </Panel>
  );
}

export default function Billing() {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const load = useCallback(() => {
    api.get("/creator/billing").then((r) => setD(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!d) return <p className="text-sm text-[#a7a0af]">Loading…</p>;
  return (
    <div data-testid="creator-billing" className="flex flex-col gap-6">
      <div>
        <h1 className="text-3xl font-extrabold">Tips & earnings</h1>
        <p className="mt-1 text-sm text-[#a7a0af]">Only your own revenue is shown here. You receive {d.creator_share_pct}% · PEEPING {d.platform_fee_pct}% (after processing fees).</p>
      </div>
      <SandboxNotice />
      <Panel title="Revenue summary">
        <RevenueTable rows={[{ id: "me", label: "Your account", r: d.revenue }]} testid="creator-revenue-table" showPayout={false} />
        <ul className="mt-3 flex flex-col gap-1 text-xs text-[#a7a0af]">
          <li data-testid="creator-by-type">Subscriptions {fmtMoney(d.by_type.subscription.gross)} · Tips {fmtMoney(d.by_type.tip.gross)} · Pay-per-view {fmtMoney(d.by_type.ppv.gross)}</li>
          <li>All Access share: {d.bundle_allocation_configured ? "allocated by the platform's configured rule" : "not yet allocated by the platform"}</li>
        </ul>
      </Panel>
      <Panel title="Tips received">
        {d.entries.filter((e) => e.revenue_type === "tip").length === 0 ? <p data-testid="tips-empty" className="text-sm text-[#a7a0af]">No tips yet. Fans can send a custom tip from your profile or during a live.</p> : (
          <ul data-testid="tips-list" className="flex flex-col gap-1 text-sm">
            {d.entries.filter((e) => e.revenue_type === "tip").map((e) => (
              <li key={e.id} className="flex justify-between border-t border-white/5 py-1.5 first:border-0">
                <span>Tip · {fmtDate(e.created_at)}</span><span>{fmtMoney(e.net)} <span className="text-[0.65rem] text-[#a7a0af]">your share</span></span>
              </li>
            ))}
          </ul>
        )}
      </Panel>
      <button onClick={() => navigate("/creator/payouts")} data-testid="billing-to-payouts" className="w-fit rounded-xl border border-white/15 px-4 py-2 text-sm">Banking & payouts →</button>
      <Panel title="Ledger entries">
        {d.entries.length === 0 ? <p className="text-sm text-[#a7a0af]">No transactions yet.</p> : (
          <ul className="flex flex-col gap-1 text-sm">
            {d.entries.map((e) => (
              <li key={e.id} className="flex justify-between border-t border-white/5 py-1.5">
                <span className="capitalize">{e.kind} · {e.revenue_type} · {fmtDate(e.created_at)}</span>
                <span>{fmtMoney(e.net)}</span>
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </div>
  );
}
